import {
  useApiClient,
  useInvalidateApiQueries,
  useInvalidateHasuraQueries,
  type QueryGroup,
  type QueryKeyUnion,
  type api,
} from '@vrplatform/data';
import { showSuccessNotification } from '@vrplatform/theme';
import { useCallback, useState } from 'react';
import { useAutomationHistoryDrawer } from '../../hooks';
import type {
  CsvImportModalv2Props,
  CsvImportModalv2SubmitValues,
} from './CsvImportModalv2';
import { parse } from 'papaparse';

type ConnectionRow =
  api['/connections']['get']['responses'][200]['content']['application/json']['data'][number];

type CsvImportResult =
  api['/csv/import']['post']['responses'][200]['content']['application/json']['data'];

type FailedCsvImportResult = Extract<CsvImportResult, { status: 'failed' }>;

type PendingImport = CsvImportModalv2SubmitValues & {
  connections: ConnectionRow[];
};

type CsvRow = Record<string, string | null | boolean | number>;

type Props = {
  uniqueRef: CsvImportModalv2Props['uniqueRef'];
  successMessage: CsvImportModalv2Props['successMessage'];
  invalidateQueryKeys: (QueryKeyUnion | (string & {}))[];
  invalidateApiQueryGroups?: QueryGroup[];
  accountId?: string;
  onSuccess?: (data: CsvRow[] | null) => void | Promise<void>;
  onCompleted: () => void | Promise<void>;
};

const ACTIVE_CONNECTION_PAGE_SIZE = 100;

const getImportErrorMessage = (result: FailedCsvImportResult) =>
  result.parseErrors?.[0]?.message ?? result.error;

export const useCsvImportModalv2Mutation = ({
  uniqueRef,
  successMessage,
  invalidateQueryKeys,
  invalidateApiQueryGroups,
  accountId,
  onSuccess,
  onCompleted,
}: Props) => {
  'use memo';

  const $api = useApiClient();
  const invalidateHasura = useInvalidateHasuraQueries(invalidateQueryKeys);
  const invalidateApi = useInvalidateApiQueries(invalidateApiQueryGroups);
  const { open: openAutomationHistoryDrawer } = useAutomationHistoryDrawer();

  const [pendingImport, setPendingImport] = useState<PendingImport | null>(
    null
  );

  const getActiveConnections = useCallback(
    async (appId: string, page = 1): Promise<ConnectionRow[]> => {
      const result = await $api.GET('/connections', {
        params: {
          query: {
            appId,
            status: 'active',
            limit: ACTIVE_CONNECTION_PAGE_SIZE,
            page,
          },
        },
      });

      if (result.error) {
        throw result.error;
      }

      const { data, pagination } = result.data;

      if (typeof pagination.nextPage !== 'number') {
        return data;
      }

      return data.concat(
        await getActiveConnections(appId, pagination.nextPage)
      );
    },
    [$api]
  );

  const runImport = useCallback(
    async (input: CsvImportModalv2SubmitValues, connectionId: string) => {
      const result = await $api.POST('/csv/import', {
        body: {
          fileData: input.fileData,
          fileName: input.fileName,
          connectionId,
          uniqueRef,
          params: {
            accountId,
            dateFormat: input.dateFormat,
          },
        },
      });

      if (result.error) {
        throw result.error;
      }

      if (result.data.data.status !== 'completed') {
        throw new Error(getImportErrorMessage(result.data.data));
      }

      return result.data.data;
    },
    [$api, accountId, uniqueRef]
  );

  const finalizeImport = useCallback(
    async (
      result: Extract<CsvImportResult, { status: 'completed' }>,
      input: CsvImportModalv2SubmitValues
    ) => {
      if (onSuccess) {
        const rows = parse<CsvRow>(input.fileData, {
          skipEmptyLines: true,
          header: true,
        });
        await Promise.resolve(onSuccess(rows.errors.length ? null : rows.data));
      } else if (result.syncId) {
        openAutomationHistoryDrawer(result.syncId);
      }

      invalidateHasura();
      invalidateApi();

      showSuccessNotification({
        title: successMessage.title,
        message: successMessage.message,
      });

      await Promise.resolve(onCompleted());
    },
    [
      invalidateApi,
      invalidateHasura,
      onCompleted,
      onSuccess,
      openAutomationHistoryDrawer,
      successMessage.message,
      successMessage.title,
    ]
  );

  const submit = useCallback(
    async (input: CsvImportModalv2SubmitValues) => {
      const connections = await getActiveConnections(input.app.id);

      if (connections.length === 1) {
        const result = await runImport(input, connections[0].id);
        await finalizeImport(result, input);
        return;
      }

      if (connections.length > 1) {
        setPendingImport({
          ...input,
          connections,
        });
        return;
      }

      const createConnection = await $api.POST('/connections', {
        body: {
          appId: input.app.id,
          name: `${input.app.name} - CSV Import`,
        },
      });

      if (createConnection.error) {
        throw createConnection.error;
      }

      const result = await runImport(input, createConnection.data.id);
      await finalizeImport(result, input);
    },
    [$api, finalizeImport, getActiveConnections, runImport]
  );

  const submitSelectedConnection = useCallback(
    async (connectionId: string) => {
      if (!pendingImport) return;

      const result = await runImport(pendingImport, connectionId);
      setPendingImport(null);
      await finalizeImport(result, pendingImport);
    },
    [finalizeImport, pendingImport, runImport]
  );

  const closeSelectConnectionModal = useCallback(() => {
    setPendingImport(null);
  }, []);

  return {
    submit,
    submitSelectedConnection,
    closeSelectConnectionModal,
    selectConnectionApp: pendingImport
      ? {
          id: pendingImport.app.id,
          name: pendingImport.app.name,
          logo: pendingImport.app.icon ?? undefined,
          extractors: pendingImport.app.extractors,
        }
      : null,
    selectConnections:
      pendingImport?.connections.map((connection) => ({
        id: connection.id,
        name: connection.name,
      })) ?? [],
  };
};
